import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { Button, Flex, Spin, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
  getBillingTopUpCheckoutSession,
  getGetAccountQueryKey,
  getGetAccountQueryOptions,
} from '../../../api/generated/api';
import { formatCredits } from '../../../modules/billing/shared/billing-format';
import { CenteredLayout, CenteredLayoutContent } from '../../../ui/components/layout';
import { hideHtmlLoader } from '../../../ui/utils/html';
import { storage } from '../../../ui/utils/local-storage';

import type { GetAccountResponseDto } from '../../../api/generated/model';
import type { GetBillingTopUpCheckoutSessionResponseDtoStatus } from '../../../api/generated/model/getBillingTopUpCheckoutSessionResponseDtoStatus';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20_000;
const TOP_UP_POLL_TIMEOUT_MS = 30_000;

const selectMembership = (account: GetAccountResponseDto) => {
  const selectedOrganizationId = storage.getJSON<Record<string, string>>('selectedOrganizationId', {})[account.id];

  return (
    account.memberships?.find(({ organization }) => organization.id === selectedOrganizationId) ??
    account.memberships?.[0]
  );
};

const SubscriptionReturn = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    const waitForSubscription = async () => {
      while (!cancelled) {
        try {
          const { data: account } = await queryClient.fetchQuery({
            ...getGetAccountQueryOptions(),
            staleTime: 0,
          });

          const selectedMembership = selectMembership(account);

          if (selectedMembership?.organization.hasActiveSubscription) {
            await router.invalidate();
            if (!cancelled) {
              await navigate({ to: '/records', replace: true });
            }

            return;
          }
        } catch {
          // Treat transient account/network failures like webhook lag and keep polling.
        }

        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          if (!cancelled) {
            setTimedOut(true);
          }

          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    };

    waitForSubscription();

    return () => {
      cancelled = true;
    };
  }, [queryClient, navigate, router, attempt]);

  return (
    <CenteredLayout>
      {timedOut ? (
        <CenteredLayoutContent
          title="Payment received"
          subtitle="Your subscription is still being activated. This usually takes a few seconds."
          width={420}
        >
          <Flex vertical gap={12}>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => {
                setTimedOut(false);
                setAttempt((current) => current + 1);
              }}
            >
              Check again
            </Button>
          </Flex>
        </CenteredLayoutContent>
      ) : (
        <CenteredLayoutContent
          title="Setting up your workspace"
          subtitle="Confirming your subscription — this only takes a moment."
          width={360}
        >
          <Flex vertical align="center" gap={16} style={{ padding: 24 }}>
            <Spin size="large" />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Don&apos;t close this tab.
            </Typography.Text>
          </Flex>
        </CenteredLayoutContent>
      )}
    </CenteredLayout>
  );
};

const TopUpReturn = ({ sessionId }: { sessionId: string | undefined }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const [outcome, setOutcome] = useState<'polling' | 'not_completed' | 'unconfirmed'>(
    sessionId ? 'polling' : 'unconfirmed',
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const startedAt = Date.now();
    let lastStatus: GetBillingTopUpCheckoutSessionResponseDtoStatus | null = null;

    const waitForCredits = async () => {
      let organizationId: string | undefined;
      while (!cancelled) {
        try {
          if (!organizationId) {
            const { data: account } = await queryClient.fetchQuery({
              ...getGetAccountQueryOptions(),
              staleTime: 0,
            });
            organizationId = selectMembership(account)?.organization.id;
          }

          if (organizationId) {
            const { data } = await getBillingTopUpCheckoutSession(organizationId, sessionId);
            lastStatus = data.status;

            if (data.status === 'paid') {
              await queryClient.refetchQueries({ queryKey: getGetAccountQueryKey() });
              await router.invalidate();
              if (cancelled) {
                return;
              }

              await navigate({ to: '/settings/billing', replace: true });
              message.success(
                `${formatCredits(data.creditsAdded ?? 0)} credits added · balance ${formatCredits(data.balance)}`,
              );

              window.dispatchEvent(new CustomEvent('billing:creditsAdded'));
              return;
            }
            if (data.status === 'expired') {
              if (!cancelled) setOutcome('not_completed');
              return;
            }
          }
        } catch {
          // Treat transient failures like webhook lag and keep polling.
        }

        if (Date.now() - startedAt > TOP_UP_POLL_TIMEOUT_MS) {
          if (!cancelled) {
            setOutcome(lastStatus === 'open' ? 'not_completed' : 'unconfirmed');
          }
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
    };

    waitForCredits();

    return () => {
      cancelled = true;
    };
  }, [queryClient, navigate, router, sessionId, attempt]);

  const backToBilling = () => navigate({ to: '/settings/billing', replace: true });

  if (outcome === 'not_completed') {
    return (
      <CenteredLayout>
        <CenteredLayoutContent title="Purchase not completed — no charge was made." width={420}>
          <Button type="primary" size="large" block onClick={backToBilling}>
            Back to billing
          </Button>
        </CenteredLayoutContent>
      </CenteredLayout>
    );
  }

  if (outcome === 'unconfirmed') {
    return (
      <CenteredLayout>
        <CenteredLayoutContent
          title="Still adding your credits"
          subtitle="We could not confirm your purchase yet. This usually takes a few seconds."
          width={420}
        >
          <Flex vertical gap={12}>
            {sessionId && (
              <Button
                type="primary"
                size="large"
                block
                onClick={() => {
                  setOutcome('polling');
                  setAttempt((current) => current + 1);
                }}
              >
                Check again
              </Button>
            )}
            <Button size="large" block onClick={backToBilling}>
              Back to billing
            </Button>
          </Flex>
        </CenteredLayoutContent>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <CenteredLayoutContent
        title="Adding your credits…"
        subtitle="Confirming your purchase — this only takes a moment."
        width={360}
      >
        <Flex vertical align="center" gap={16} style={{ padding: 24 }}>
          <Spin size="large" />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Don&apos;t close this tab.
          </Typography.Text>
        </Flex>
      </CenteredLayoutContent>
    </CenteredLayout>
  );
};

const BillingReturnRoute = () => {
  const { session_id: sessionId, kind } = Route.useSearch();
  return kind === 'top_up' ? <TopUpReturn sessionId={sessionId} /> : <SubscriptionReturn />;
};

export const Route = createFileRoute('/_authenticated/billing/return')({
  validateSearch: z.object({
    session_id: z.string().optional(),
    kind: z.string().optional(),
  }),
  beforeLoad: () => hideHtmlLoader(),
  component: BillingReturnRoute,
});
